import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import {
    NavLink as RouterLink,
    matchPath,
    useLocation,
} from "react-router-dom";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
// import materialIcons from "material-design-icons/iconfont/material-icons.css";
import "material-design-icons/iconfont/material-icons.css";
// material
import { alpha, useTheme, styled } from "@mui/material/styles";
import {
    Box,
    List,
    Collapse,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    Tooltip,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    Typography,
    CircularProgress,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { useDispatch, useSelector } from "react-redux";
import { get, post } from "src/services/content.service";
import { useTranslation } from "react-i18next";
import { showAlert } from "src/redux/slices/AlertSlice";
import materialIconNames from "src/data/materialIconNames";
import axios from "axios";

// ----------------------------------------------------------------------

const ListItemStyle = styled(
    React.forwardRef((props, ref) => (
        <ListItemButton ref={ref} disableGutters {...props} />
    ))
)(({ theme }) => ({
    ...theme.typography.body2,
    height: 48,
    position: "relative",
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    "&:before": {
        top: 0,
        right: 0,
        width: 3,
        bottom: 0,
        content: "''",
        display: "none",
        position: "absolute",
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
        backgroundColor: theme.palette.primary.main,
    },
}));

const ListItemIconStyle = styled(ListItemIcon)({
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});

const resolveFileUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
    }
    const baseUrl = axios.defaults.baseURL || "";
    if (!baseUrl) return path;
    if (path.startsWith("/")) {
        return `${baseUrl}${path}`;
    }
    return `${baseUrl}/${path}`;
};

// ----------------------------------------------------------------------

NavItem.propTypes = {
    item: PropTypes.object,
    active: PropTypes.func,
    onContextMenu: PropTypes.func,
    t: PropTypes.func,
    sortableRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    sortableStyle: PropTypes.object,
    dragHandleProps: PropTypes.object,
    isDragging: PropTypes.bool,
    isOver: PropTypes.bool,
    blockClick: PropTypes.bool,
    renderPrefix: PropTypes.func,
    activeOverride: PropTypes.bool,
    isSubItem: PropTypes.bool,
};

function NavItem({
    item,
    active,
    onContextMenu,
    t,
    sortableRef,
    sortableStyle,
    dragHandleProps,
    isDragging,
    isOver,
    blockClick,
    renderPrefix,
    activeOverride,
    isSubItem,
}) {
    const theme = useTheme();
    const isActiveRoot = active(item.path);
    const { title, path, icon, info, children, tooltip } = item;
    const [open, setOpen] = useState(isActiveRoot);
    const isActive =
        typeof activeOverride === "boolean" ? activeOverride : isActiveRoot;

    const handleOpen = () => {
        setOpen((prev) => !prev);
    };

    const handleItemClick = (event) => {
        if (blockClick) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (children) {
            handleOpen();
        }
    };

    const activeRootStyle = {
        color: "primary.main",
        fontWeight: "fontWeightMedium",
        bgcolor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity
        ),
        "&:before": { display: "block" },
    };

    const activeSubStyle = {
        color: "text.primary",
        fontWeight: "fontWeightMedium",
    };
    const activeStyle = isSubItem ? activeSubStyle : activeRootStyle;

    const iconFilePathRaw =
        item.iconFile && (item.iconFile.filePath || item.iconFile.file_path);
    const iconFilePath = resolveFileUrl(iconFilePathRaw);
    const iconNode = iconFilePath ? (
        <Box
            component="img"
            src={iconFilePath}
            alt={title}
            sx={{ width: 20, height: 20, objectFit: "contain" }}
        />
    ) : icon ? (
        <span className="material-icons">{icon}</span>
    ) : null;

    if (children) {
        return (
            <>
                <ListItemStyle
                    onClick={handleItemClick}
                    onContextMenu={
                        item.contextMenuEnabled && onContextMenu
                            ? (event) => onContextMenu(event, item)
                            : undefined
                    }
                    ref={sortableRef}
                    style={sortableStyle}
                    sx={{
                        ...(isActive && activeStyle),
                        ...(isOver && {
                            bgcolor: alpha(
                                theme.palette.primary.main,
                                theme.palette.action.hoverOpacity
                            ),
                        }),
                        ...(isDragging && { opacity: 0.6 }),
                    }}
                >
                    {item.contextMenuEnabled && dragHandleProps && (
                        <Tooltip title={t ? t("NavSection.ContextMenu.dragHint") : ""}>
                            <Box
                                component="span"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mr: 1,
                                    color: "text.disabled",
                                    cursor: "grab",
                                    touchAction: "none",
                                }}
                                {...dragHandleProps}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <DragIndicatorIcon fontSize="small" />
                            </Box>
                        </Tooltip>
                    )}
                    {renderPrefix ? renderPrefix() : (
                        <ListItemIconStyle>
                            {iconNode || <Box sx={{ width: 20, height: 20 }} />}
                        </ListItemIconStyle>
                    )}
                    <Tooltip title={tooltip ? tooltip : ""} placement="right">
                        <ListItemText disableTypography primary={title} />
                    </Tooltip>
                    {info && info}
                    <Box
                        component={Icon}
                        icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                        sx={{ width: 16, height: 16, ml: 1 }}
                    />
                </ListItemStyle>

                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List
                        component="div"
                        disablePadding
                        sx={{
                            pl: 2,
                            borderLeft: (theme) =>
                                `1px solid ${alpha(
                                    theme.palette.primary.main,
                                    0.15
                                )}`,
                            ml: 2.5,
                        }}
                    >
                        <SortableContext
                            items={children
                                .filter((child) => child && child.navId != null)
                                .map((child) => child.navId)}
                            strategy={verticalListSortingStrategy}
                        >
                            {children.map((child) => {
                                const { title, path } = child;
                                const isActiveSub = active(path);
                                const childIconFilePathRaw =
                                    child.iconFile &&
                                    (child.iconFile.filePath ||
                                        child.iconFile.file_path);
                                const childIconFilePath = resolveFileUrl(
                                    childIconFilePathRaw
                                );
                                const childHasIcon =
                                    Boolean(childIconFilePath) || Boolean(child.icon);
                                return (
                                    <SortableNavItem
                                        key={child.navId || title}
                                        item={{
                                            ...child,
                                            info: null,
                                            children: null,
                                            tooltip: child.tooltip,
                                            contextMenuEnabled: child.contextMenuEnabled,
                                        }}
                                        active={active}
                                        onContextMenu={onContextMenu}
                                        t={t}
                                        sortableEnabled={true}
                                        blockClick={blockClick}
                                        isSubItem
                                        renderPrefix={() => (
                                            <ListItemIconStyle>
                                                {childHasIcon ? (
                                                    childIconFilePath ? (
                                                        <Box
                                                            component="img"
                                                            src={childIconFilePath}
                                                            alt={title}
                                                            sx={{
                                                                width: 18,
                                                                height: 18,
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="material-icons">
                                                            {child.icon}
                                                        </span>
                                                    )
                                                ) : (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            width: 4,
                                                            height: 4,
                                                            display: "flex",
                                                            borderRadius: "50%",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            bgcolor: "text.disabled",
                                                            transition: (theme) =>
                                                                theme.transitions.create(
                                                                    "transform"
                                                                ),
                                                            ...(isActiveSub && {
                                                                transform: "scale(2)",
                                                                bgcolor: "primary.main",
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </ListItemIconStyle>
                                        )}
                                        activeOverride={isActiveSub}
                                    />
                                );
                            })}
                        </SortableContext>
                    </List>
                </Collapse>
            </>
        );
    }

    return (
        <ListItemStyle
            component={RouterLink}
            to={path}
            onClick={handleItemClick}
            onContextMenu={
                item.contextMenuEnabled && onContextMenu
                    ? (event) => onContextMenu(event, item)
                    : undefined
            }
            ref={sortableRef}
            style={sortableStyle}
            sx={{
                ...(isActive && activeStyle),
                ...(isOver && {
                    bgcolor: alpha(
                        theme.palette.primary.main,
                        theme.palette.action.hoverOpacity
                    ),
                }),
                ...(isDragging && { opacity: 0.6 }),
            }}
        >
            {item.contextMenuEnabled && dragHandleProps && (
                <Tooltip title={t ? t("NavSection.ContextMenu.dragHint") : ""}>
                    <Box
                        component="span"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            mr: 1,
                            color: "text.disabled",
                            cursor: "grab",
                            touchAction: "none",
                        }}
                        {...dragHandleProps}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <DragIndicatorIcon fontSize="small" />
                    </Box>
                </Tooltip>
            )}
            {renderPrefix ? renderPrefix() : (
                <ListItemIconStyle>
                    {iconNode || <Box sx={{ width: 20, height: 20 }} />}
                </ListItemIconStyle>
            )}
            <Tooltip title={tooltip ? tooltip : ""} placement="right">
                <ListItemText disableTypography primary={title} />
            </Tooltip>
            {info && info}
        </ListItemStyle>
    );
}

NavSection.propTypes = {
    navConfig: PropTypes.array,
};

// const getIcon = (name) => <Icon icon={name} width={22} height={22} />;
// const getIcon = (icon) => <Icon>{icon}</Icon>;
export default function NavSection({ navConfig, adminConfig, ...other }) {
    const { pathname } = useLocation();
    const match = (path) =>
        path ? !!matchPath({ path, end: false }, pathname) : false;

    const [customNavItems, setCustomNavItems] = useState([]);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user && user.is_staff === true;
    const tempIdRef = useRef(-1);

    const allowedParentTypes = ["content_list", "nested_list", "tab", "list"];
    const rootAllowedTypes = ["content_list", "nested_list", "tab", "list", "content"];
    const parentChildRules = {
        nested_list: ["tab", "content_list", "list", "content"],
        tab: ["content_list", "content"],
        content_list: ["content"],
        list: ["content_list", "content"],
    };

    const typeLabels = {
        content_list: t("AddNodeDialog.NavTypes.content_list"),
        tab: t("AddNodeDialog.NavTypes.tab"),
        nested_list: t("AddNodeDialog.NavTypes.nested_list"),
        list: t("AddNodeDialog.NavTypes.list"),
        content: t("AddNodeDialog.NavTypes.content"),
    };

    const nextTempId = () => {
        tempIdRef.current -= 1;
        return tempIdRef.current;
    };

    const mapNavItem = (navItem, isAdminMenu) => {
        const navType = navItem.content.type;
        let path = null;
        let children = null;
        if (navType === "nested_list" && Array.isArray(navItem.children)) {
            children = navItem.children.map((child) => {
                const queryParam =
                    child.content.type === "tab" ? "tab" : "page";
                return {
                    title: child.content.title,
                    path:
                        child.content.type === "content"
                            ? `/dashboard/content/${child.content.slug}/`
                            : `/dashboard/contents/${child.content.slug}?${queryParam}=1`,
                    navId: child.id,
                    parentId: child.parent,
                    type: child.content.type,
                    position: child.position,
                    children: null,
                    icon: child.icon,
                    iconFile: child.icon_file || null,
                    contextMenuEnabled: isAdminMenu,
                };
            });
        } else {
            if (navItem.content.type === "content") {
                path = `/dashboard/content/${navItem.content.slug}/`;
            } else {
                const queryParam =
                    navItem.content.type === "tab" ? "tab" : "page";
                path = `/dashboard/contents/${navItem.content.slug}?${queryParam}=1`;
            }
        }
        return {
            title: navItem.content.title,
            path: path,
            icon: navItem.icon,
            iconFile: navItem.icon_file || null,
            children: children,
            tooltip: navItem.content.tooltip,
            navId: navItem.id,
            parentId: navItem.parent,
            type: navItem.content.type,
            position: navItem.position,
            status: navItem.content.status,
            contextMenuEnabled: isAdminMenu,
        };
    };

    const fetchCustomNavItems = useCallback(async () => {
        try {
            const res = await get(dispatch, `/api/content/nav/items`);
            const tempNavArray = [];
            for (const navItem of res.data) {
                tempNavArray.push(mapNavItem(navItem, isAdmin));
            }
            setCustomNavItems(tempNavArray);
        } catch (err) {
            console.log(err);
        }
    }, [dispatch, isAdmin]);

    const loadCustomIcons = useCallback(async () => {
        setCustomIconsLoading(true);
        try {
            const res = await get(dispatch, `/api/content/nav/icons`);
            setCustomIcons(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconLoadError"),
                    severity: "error",
                })
            );
        } finally {
            setCustomIconsLoading(false);
        }
    }, [dispatch, t]);

    const handleIconUpload = async (file) => {
        if (!file) return;
        if (!["image/png", "image/svg+xml"].includes(file.type)) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconTypeError"),
                    severity: "error",
                })
            );
            return;
        }
        setUploadingIcon(true);
        try {
            const reader = new FileReader();
            const dataUrl = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const payload = {
                file: {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    file: dataUrl,
                },
            };
            const res = await post(dispatch, `/api/content/nav/icons`, payload);
            const newIcon = res.data;
            setCustomIcons((prev) => [newIcon, ...prev]);
            setIconMode("custom");
            setSelectedIconFile(newIcon);
            setSelectedIcon("");
        } catch (err) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconUploadError"),
                    severity: "error",
                })
            );
        } finally {
            setUploadingIcon(false);
        }
    };

    useEffect(() => {
        fetchCustomNavItems();
        return () => {
            setCustomNavItems([]);
        };
    }, [fetchCustomNavItems]);

    const [contextAnchorEl, setContextAnchorEl] = useState(null);
    const [contextItem, setContextItem] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState(null);
    const [nodeTitle, setNodeTitle] = useState("");
    const [nodeType, setNodeType] = useState("");
    const [nodeActive, setNodeActive] = useState(true);
    const [typeOptions, setTypeOptions] = useState([]);
    const [iconMode, setIconMode] = useState("none");
    const [iconSearch, setIconSearch] = useState("");
    const [customSearch, setCustomSearch] = useState("");
    const [customIcons, setCustomIcons] = useState([]);
    const [customIconsLoading, setCustomIconsLoading] = useState(false);
    const [selectedIcon, setSelectedIcon] = useState("");
    const [selectedIconFile, setSelectedIconFile] = useState(null);
    const [uploadingIcon, setUploadingIcon] = useState(false);
    const [activeNavId, setActiveNavId] = useState(null);
    const [isNavDragging, setIsNavDragging] = useState(false);
    const [suppressClick, setSuppressClick] = useState(false);
    const initialOrderRef = useRef(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    const navIndex = useMemo(() => {
        const index = {};
        const walkItems = (items) => {
            if (!Array.isArray(items)) return;
            items.forEach((item) => {
                if (item.navId != null) {
                    index[item.navId] = item;
                }
                if (Array.isArray(item.children)) {
                    walkItems(item.children);
                }
            });
        };
        walkItems(customNavItems);
        return index;
    }, [customNavItems]);

    const filteredPredefinedIcons = useMemo(() => {
        const term = iconSearch.trim().toLowerCase();
        if (!term) return materialIconNames;
        return materialIconNames.filter((name) =>
            name.toLowerCase().includes(term)
        );
    }, [iconSearch]);

    const filteredCustomIcons = useMemo(() => {
        const term = customSearch.trim().toLowerCase();
        if (!term) return customIcons;
        return customIcons.filter((icon) =>
            (icon.fileName || "").toLowerCase().includes(term)
        );
    }, [customIcons, customSearch]);

    const closeContextMenu = () => {
        setContextAnchorEl(null);
    };

    const dismissContextMenu = () => {
        setContextAnchorEl(null);
        setContextItem(null);
    };

    const handleContextMenu = (event, item) => {
        if (!isAdmin) {
            return;
        }
        event.preventDefault();
        setContextAnchorEl(event.currentTarget);
        setContextItem(item);
    };

    const resolveParentType = (parentId) => {
        if (parentId == null) {
            return null;
        }
        const parent = navIndex[parentId];
        return parent ? parent.type : null;
    };

    const getChildrenCount = (parentId) => {
        if (parentId == null) {
            return Array.isArray(customNavItems) ? customNavItems.length : 0;
        }
        const parent = navIndex[parentId];
        if (!parent || !Array.isArray(parent.children)) {
            return 0;
        }
        return parent.children.length;
    };

    const openRenameDialog = () => {
        if (!contextItem) return;
        setDialogMode("rename");
        setNodeTitle(contextItem.title || "");
        setNodeType(contextItem.type || "");
        setNodeActive(contextItem.status !== 0);
        setTypeOptions([]);
        if (contextItem.iconFile) {
            setIconMode("custom");
            setSelectedIconFile(contextItem.iconFile);
            setSelectedIcon("");
        } else if (contextItem.icon) {
            setIconMode("predefined");
            setSelectedIcon(contextItem.icon);
            setSelectedIconFile(null);
        } else {
            setIconMode("none");
            setSelectedIcon("");
            setSelectedIconFile(null);
        }
        loadCustomIcons();
        setDialogOpen(true);
        closeContextMenu();
    };

    const openAddDialog = (mode) => {
        if (!contextItem) return;
        const parentId =
            mode === "addInside" ? contextItem.navId : contextItem.parentId;
        const parentType = resolveParentType(parentId);
        if (parentType !== null && !allowedParentTypes.includes(parentType)) {
            dispatch(
                showAlert({
                    message: t("NavigationStep.Errors.parentType"),
                    severity: "error",
                })
            );
            closeContextMenu();
            return;
        }
        const allowedTypes =
            parentType === null
                ? rootAllowedTypes
                : parentChildRules[parentType] || [];
        const options = allowedTypes
            .filter((type) => typeLabels[type])
            .map((type) => ({ value: type, label: typeLabels[type] }));
        if (!options.length) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.noTypes"),
                    severity: "error",
                })
            );
            closeContextMenu();
            return;
        }
        setDialogMode(mode);
        setNodeTitle("");
        setNodeType(options[0].value);
        setNodeActive(true);
        setTypeOptions(options);
        setIconMode("none");
        setSelectedIcon("");
        setSelectedIconFile(null);
        loadCustomIcons();
        setDialogOpen(true);
        closeContextMenu();
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setDialogMode(null);
        setNodeTitle("");
        setNodeType("");
        setNodeActive(true);
        setTypeOptions([]);
        setIconMode("none");
        setIconSearch("");
        setCustomSearch("");
        setSelectedIcon("");
        setSelectedIconFile(null);
        setContextItem(null);
    };

    const submitDialog = async () => {
        if (!contextItem) {
            closeDialog();
            return;
        }
        const title = nodeTitle.trim();
        if (!title) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.emptyName"),
                    severity: "error",
                })
            );
            return;
        }
        if (iconMode === "custom" && !selectedIconFile) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconMissing"),
                    severity: "error",
                })
            );
            return;
        }
        const iconValue = iconMode === "predefined" ? selectedIcon : "";
        const iconFileId =
            iconMode === "custom" && selectedIconFile
                ? selectedIconFile.id
                : null;

        let modified = null;
        if (dialogMode === "rename") {
            if (title === contextItem.title) {
                if (
                    iconValue === (contextItem.icon || "") &&
                    iconFileId === (contextItem.iconFile?.id || null)
                ) {
                    closeDialog();
                    return;
                }
            }
            modified = {
                id: contextItem.navId,
                title,
                type: contextItem.type,
                parent: contextItem.parentId ?? null,
                children: [],
                position: contextItem.position ?? 0,
                icon: iconValue,
                iconFileId: iconFileId,
                changed: true,
            };
        } else if (dialogMode === "addInside" || dialogMode === "addSibling") {
            const parentId =
                dialogMode === "addInside"
                    ? contextItem.navId
                    : contextItem.parentId ?? null;
            const position = getChildrenCount(parentId);
            modified = {
                id: nextTempId(),
                title,
                type: nodeType,
                parent: parentId,
                children: [],
                position,
                status: nodeActive ? 1 : 0,
                icon: iconValue,
                iconFileId: iconFileId,
                changed: true,
            };
        }

        if (!modified) {
            closeDialog();
            return;
        }

        const payload = {
            data: {
                userId: user ? user.id : null,
                id: -1,
                type: "navigation",
                status: false,
                title: "",
                body: null,
                sticky: 0,
                news: true,
                groups: [],
            },
            nav: {
                modified: [modified],
                removed: [],
            },
        };

        try {
            await post(dispatch, `/api/content/admin/add/`, payload);
            dispatch(
                showAlert({
                    message:
                        dialogMode === "rename"
                            ? t("NavSection.ContextMenu.editSuccess")
                            : t("NavSection.ContextMenu.addSuccess"),
                    severity: "success",
                })
            );
            closeDialog();
            fetchCustomNavItems();
        } catch (err) {
            dispatch(
                showAlert({
                    message:
                        (err.response &&
                            err.response.data &&
                            (err.response.data.nav || err.response.data.error)) ||
                        err.message ||
                        t("NavSection.ContextMenu.saveError"),
                    severity: "error",
                })
            );
        }
    };

    const findNodeById = (items, id) => {
        if (!Array.isArray(items)) return null;
        for (const item of items) {
            if (item.navId === id) {
                return item;
            }
            if (Array.isArray(item.children)) {
                const found = findNodeById(item.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const getChildrenByParentId = (items, parentId) => {
        if (parentId == null) {
            return Array.isArray(items) ? items : [];
        }
        const parent = findNodeById(items, parentId);
        return parent && Array.isArray(parent.children) ? parent.children : [];
    };

    const reorderItemsById = (items, fromId, toId) => {
        if (!Array.isArray(items)) {
            return items;
        }
        const fromIndex = items.findIndex((item) => item.navId === fromId);
        const toIndex = items.findIndex((item) => item.navId === toId);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
            return items;
        }
        return arrayMove(items, fromIndex, toIndex);
    };

    const applyReorderByParent = (items, parentId, fromId, toId) => {
        if (parentId == null) {
            return reorderItemsById(items, fromId, toId);
        }
        return items.map((item) => {
            if (item.navId !== parentId) {
                return item;
            }
            return {
                ...item,
                children: reorderItemsById(item.children || [], fromId, toId),
            };
        });
    };

    const getParentIdByNavId = (navId) => {
        const node = navIndex[navId];
        return node ? node.parentId ?? null : null;
    };

    const handleNavDragStart = ({ active }) => {
        if (!isAdmin) return;
        setIsNavDragging(true);
        setActiveNavId(active.id);
        const parentId = getParentIdByNavId(active.id);
        const siblings = getChildrenByParentId(customNavItems, parentId);
        initialOrderRef.current = new Map(
            siblings.map((sibling, idx) => [sibling.navId, idx])
        );
    };

    const handleNavDragOver = ({ active, over }) => {
        if (!isAdmin || !over || active.id === over.id) {
            return;
        }
        const activeParent = getParentIdByNavId(active.id);
        const overParent = getParentIdByNavId(over.id);
        if (activeParent !== overParent) {
            return;
        }
        setCustomNavItems((prev) =>
            applyReorderByParent(prev, activeParent, active.id, over.id)
        );
    };

    const handleNavDragCancel = () => {
        setActiveNavId(null);
        setIsNavDragging(false);
    };

    const handleNavDragEnd = async ({ active, over }) => {
        setActiveNavId(null);
        setIsNavDragging(false);
        setSuppressClick(true);
        setTimeout(() => {
            setSuppressClick(false);
        }, 0);
        if (!isAdmin || !over) {
            return;
        }
        const activeParent = getParentIdByNavId(active.id);
        const overParent = getParentIdByNavId(over.id);
        if (activeParent !== overParent) {
            return;
        }
        const siblings = getChildrenByParentId(customNavItems, activeParent);
        if (!Array.isArray(siblings) || siblings.length < 2) {
            return;
        }
        const oldPositions = initialOrderRef.current || new Map();
        const modified = siblings
            .map((node, idx) => ({
                id: node.navId,
                title: node.title,
                type: node.type,
                parent: node.parentId ?? null,
                children: [],
                position: idx,
                changed: true,
                _old: oldPositions.get(node.navId),
            }))
            .filter((node) => node._old !== node.position)
            .map(({ _old, ...node }) => node);

        if (!modified.length) {
            return;
        }

        const payload = {
            data: {
                userId: user ? user.id : null,
                id: -1,
                type: "navigation",
                status: false,
                title: "",
                body: null,
                sticky: 0,
                news: true,
                groups: [],
            },
            nav: {
                modified,
                removed: [],
            },
        };

        try {
            const res = await post(dispatch, `/api/content/admin/add/`, payload);
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.moveSuccess"),
                    severity: "success",
                })
            );
        } catch (err) {
            dispatch(
                showAlert({
                    message:
                        (err.response &&
                            err.response.data &&
                            (err.response.data.nav || err.response.data.error)) ||
                        err.message ||
                        t("NavSection.ContextMenu.saveError"),
                    severity: "error",
                })
            );
            fetchCustomNavItems();
        }
    };

    const blockClick = isNavDragging || suppressClick;
    const activeNavItem = activeNavId ? navIndex[activeNavId] : null;

    return (
        <Box {...other}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragStart={handleNavDragStart}
                onDragOver={handleNavDragOver}
                onDragEnd={handleNavDragEnd}
                onDragCancel={handleNavDragCancel}
            >
                <List disablePadding>
                    {adminConfig.map((item) => (
                        <NavItem key={item.title} item={item} active={match} t={t} />
                    ))}
                    {navConfig.map((item) => (
                        <NavItem key={item.title} item={item} active={match} t={t} />
                    ))}
                    <SortableContext
                        items={customNavItems
                            .filter((item) => item && item.navId != null)
                            .map((item) => item.navId)}
                        strategy={verticalListSortingStrategy}
                    >
                        {customNavItems.map((item) => (
                            <SortableNavItem
                                key={item.navId || item.title}
                                item={item}
                                active={match}
                                onContextMenu={handleContextMenu}
                                t={t}
                                sortableEnabled={isAdmin}
                                blockClick={blockClick}
                            />
                        ))}
                    </SortableContext>
                </List>
                <DragOverlay>
                    {activeNavItem ? (
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                borderRadius: 1.25,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: 6,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <DragIndicatorIcon fontSize="small" />
                            <ListItemText
                                disableTypography
                                primary={activeNavItem.title}
                                sx={{ color: "text.primary" }}
                            />
                        </Box>
                    ) : null}
                </DragOverlay>
            </DndContext>
            <Menu
                anchorEl={contextAnchorEl}
                open={Boolean(contextAnchorEl)}
                onClose={dismissContextMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <MenuItem onClick={openRenameDialog}>
                    {t("buttonCommons.edit")}
                </MenuItem>
                <MenuItem onClick={() => openAddDialog("addInside")}>
                    {t("NavSection.ContextMenu.addInside")}
                </MenuItem>
                <MenuItem onClick={() => openAddDialog("addSibling")}>
                    {t("NavSection.ContextMenu.addSibling")}
                </MenuItem>
            </Menu>
            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {dialogMode === "rename"
                        ? t("NavSection.ContextMenu.renameTitle")
                        : t("NavSection.ContextMenu.addTitle")}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label={t("NavSection.ContextMenu.nameLabel")}
                        value={nodeTitle}
                        onChange={(event) => setNodeTitle(event.target.value)}
                    />
                    {dialogMode !== "rename" && (
                        <>
                            <FormControl fullWidth margin="dense">
                                <InputLabel>
                                    {t("NavSection.ContextMenu.typeLabel")}
                                </InputLabel>
                                <Select
                                    value={nodeType}
                                    label={t("NavSection.ContextMenu.typeLabel")}
                                    onChange={(event) =>
                                        setNodeType(event.target.value)
                                    }
                                >
                                    {typeOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={nodeActive}
                                        onChange={(event) =>
                                            setNodeActive(event.target.checked)
                                        }
                                    />
                                }
                                label={t("NavSection.ContextMenu.activeLabel")}
                            />
                        </>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t("NavSection.ContextMenu.iconLabel")}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Button
                                variant={
                                    iconMode === "none"
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() => {
                                    setIconMode("none");
                                    setSelectedIcon("");
                                    setSelectedIconFile(null);
                                }}
                                size="small"
                            >
                                {t("NavSection.ContextMenu.iconNone")}
                            </Button>
                            <Button
                                variant={
                                    iconMode === "predefined"
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() => {
                                    setIconMode("predefined");
                                    setSelectedIconFile(null);
                                }}
                                size="small"
                            >
                                {t("NavSection.ContextMenu.iconPredefined")}
                            </Button>
                            <Button
                                variant={
                                    iconMode === "custom"
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() => setIconMode("custom")}
                                size="small"
                            >
                                {t("NavSection.ContextMenu.iconCustom")}
                            </Button>
                        </Stack>
                        {iconMode === "predefined" ? (
                            <>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label={t("NavSection.ContextMenu.iconSearch")}
                                    value={iconSearch}
                                    onChange={(event) =>
                                        setIconSearch(event.target.value)
                                    }
                                />
                                <Box
                                    sx={{
                                        mt: 1,
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fill, minmax(44px, 1fr))",
                                        gap: 1,
                                        maxHeight: 200,
                                        overflowY: "auto",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        p: 1,
                                    }}
                                >
                                    {filteredPredefinedIcons.map((iconName) => (
                                        <Box
                                            key={iconName}
                                            onClick={() => {
                                                setSelectedIcon(iconName);
                                                setSelectedIconFile(null);
                                            }}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                border: "1px solid",
                                                borderColor:
                                                    selectedIcon === iconName
                                                        ? "primary.main"
                                                        : "transparent",
                                                bgcolor:
                                                    selectedIcon === iconName
                                                        ? "action.selected"
                                                        : "transparent",
                                            }}
                                        >
                                            <span className="material-icons">
                                                {iconName}
                                            </span>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        ) : iconMode === "custom" ? (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <TextField
                                        fullWidth
                                        margin="dense"
                                        label={t("NavSection.ContextMenu.iconSearch")}
                                        value={customSearch}
                                        onChange={(event) =>
                                            setCustomSearch(event.target.value)
                                        }
                                    />
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                        disabled={uploadingIcon}
                                    >
                                        {t("NavSection.ContextMenu.iconUpload")}
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/png,image/svg+xml"
                                            onChange={(event) => {
                                                const file =
                                                    event.target.files &&
                                                    event.target.files[0];
                                                if (file) {
                                                    handleIconUpload(file);
                                                }
                                                event.target.value = "";
                                            }}
                                        />
                                    </Button>
                                    {uploadingIcon && (
                                        <CircularProgress size={20} />
                                    )}
                                </Stack>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fill, minmax(56px, 1fr))",
                                        gap: 1,
                                        maxHeight: 200,
                                        overflowY: "auto",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        p: 1,
                                    }}
                                >
                                    {customIconsLoading ? (
                                        <Box
                                            sx={{
                                                gridColumn: "1 / -1",
                                                display: "flex",
                                                justifyContent: "center",
                                                py: 2,
                                            }}
                                        >
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        filteredCustomIcons.map((iconFile) => {
                                            const filePathRaw =
                                                iconFile.filePath ||
                                                iconFile.file_path;
                                            const filePath = resolveFileUrl(
                                                filePathRaw
                                            );
                                            return (
                                                <Box
                                                    key={iconFile.id}
                                                    onClick={() => {
                                                        setSelectedIconFile(
                                                            iconFile
                                                        );
                                                        setSelectedIcon("");
                                                    }}
                                                    sx={{
                                                        width: 52,
                                                        height: 52,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: 1,
                                                        cursor: "pointer",
                                                        border: "1px solid",
                                                        borderColor:
                                                            selectedIconFile &&
                                                            selectedIconFile.id ===
                                                                iconFile.id
                                                                ? "primary.main"
                                                                : "transparent",
                                                        bgcolor:
                                                            selectedIconFile &&
                                                            selectedIconFile.id ===
                                                                iconFile.id
                                                                ? "action.selected"
                                                                : "transparent",
                                                    }}
                                                >
                                                    {filePath ? (
                                                        <Box
                                                            component="img"
                                                            src={filePath}
                                                            alt={iconFile.fileName}
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    ) : null}
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            </>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>
                        {t("buttonCommons.cancel")}
                    </Button>
                    <Button onClick={submitDialog}>
                        {t("buttonCommons.save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

SortableNavItem.propTypes = {
    item: PropTypes.object.isRequired,
    active: PropTypes.func.isRequired,
    onContextMenu: PropTypes.func,
    t: PropTypes.func,
    sortableEnabled: PropTypes.bool,
    blockClick: PropTypes.bool,
    renderPrefix: PropTypes.func,
    activeOverride: PropTypes.bool,
    isSubItem: PropTypes.bool,
};

function SortableNavItem({
    item,
    active,
    onContextMenu,
    t,
    sortableEnabled,
    blockClick,
    renderPrefix,
    activeOverride,
    isSubItem,
}) {
    const disabled =
        !sortableEnabled || !item.contextMenuEnabled || item.navId == null;
    const sortableId = item.navId ?? item.title;
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
        isOver,
    } = useSortable({ id: sortableId, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const dragHandleProps = disabled
        ? null
        : {
              ...attributes,
              ...listeners,
              ref: setActivatorNodeRef,
          };
    return (
        <NavItem
            item={item}
            active={active}
            onContextMenu={onContextMenu}
            t={t}
            sortableRef={setNodeRef}
            sortableStyle={style}
            dragHandleProps={dragHandleProps}
            isDragging={isDragging}
            isOver={isOver}
            blockClick={blockClick}
            renderPrefix={renderPrefix}
            activeOverride={activeOverride}
            isSubItem={isSubItem}
        />
    );
}
